import { JepGraphic } from '@jeppesen-foreflight/jmkw-types';
import * as stringUtils from '../../../../utilities/string-format.utils';
import { NavigationFeatureType } from '../navigation-feature-type';
import { FeatureDetail } from './feature-detail';
import { NavaidAttributesRaw } from './navaid-attributes-raw';
import { NavaidAttributes } from './navaid-attributes';

export class Navaid implements FeatureDetail {
  id: string;
  navType: NavigationFeatureType = 'navaid';
  attributes: NavaidAttributes;
  typeflags: string[];

  private readonly SERVICE_VOLUME_MAP: Record<string, string> = {
    H: 'High Altitude',
    T: 'Terminal',
    U: 'Unrestricted',
    L: 'Low Altitude',
    B: 'High and Low Altitude'
  };

  private readonly AIRWAY_TYPE_MAP: Record<string, string> = {
    H: 'High Airway Endpoint',
    L: 'Low Airway Endpoint',
    B: 'High and Low Airway Endpoint'
  };

  constructor(public graphic: JepGraphic) {
    this.typeflags = this._getTypeflags(graphic?.attributes);
    this.attributes = this._formatAttributes(graphic?.attributes);
    this.id = this.attributes.id;
  }

  private _getTypeflags(attrs?: NavaidAttributesRaw): string[] {
    const type = stringUtils.formatString(attrs?.type);
    return type ? type.split('/') : [];
  }

  private _formatAttributes(attrs?: NavaidAttributesRaw): NavaidAttributes {
    if (!attrs) {
      return {
        angle: '',
        coordinates: '',
        sourcelatitudedd: '',
        sourcelongitudedd: '',
        dmeChannel: '',
        frequency: '',
        id: '',
        isOnAirway: '',
        minCrossingAlt: '',
        morseCode: '',
        name: '',
        navaidType: '',
        serviceVolume: '',
        stationDeclinationDegrees: '',
        type: ''
      };
    }

    return {
      angle: stringUtils.formatBearing(attrs.angle),
      coordinates: stringUtils.formatLatLong(attrs.latitude, attrs.longitude),
      sourcelatitudedd: attrs.sourcelatitudedd ?? '',
      sourcelongitudedd: attrs.sourcelongitudedd ?? '',
      dmeChannel: stringUtils.formatString(attrs.dmechannel),
      frequency: this._formatFrequency(
        attrs.frequency,
        attrs.frequency_unit_code
      ),
      id: stringUtils.formatString(attrs.navaid_id),
      isOnAirway: this._formatAirwayType(attrs.isonairway),
      minCrossingAlt: this._formatMCA(attrs.mca),
      morseCode: stringUtils.formatString(attrs.morsecode),
      name: stringUtils.formatString(attrs.name),
      navaidType: stringUtils.formatString(attrs.navaid_type),
      serviceVolume: this._formatServiceVolume(attrs.servicevolume),
      stationDeclinationDegrees: stringUtils.formatMagneticVariation(
        attrs.station_declination_deg
      ),
      type: stringUtils.formatString(attrs.type)
    };
  }

  private _formatFrequency(
    frequency?: string | null,
    unitCode?: string | null
  ): string {
    const freq = stringUtils.formatString(frequency);
    const unit = stringUtils.formatString(unitCode);
    return freq ? `${freq}${unit}` : '';
  }

  private _formatServiceVolume(volume?: string | null): string {
    const formattedVolume = stringUtils.formatString(volume);
    return this.SERVICE_VOLUME_MAP[formattedVolume] || formattedVolume;
  }

  private _formatAirwayType(type?: string | null): string {
    const formattedType = stringUtils.formatString(type);
    return this.AIRWAY_TYPE_MAP[formattedType] || '';
  }

  private _formatMCA(mca?: string | null): string {
    const formattedMCA = stringUtils.formatString(mca);
    if (!formattedMCA) return '';
    return isNaN(+formattedMCA)
      ? formattedMCA
      : stringUtils.formatAltitude(+formattedMCA);
  }
}
